/**
 * Public type surface shared between the core and the React layer.
 *
 * Consolidates what used to live in src/models/*.ts (one type per file). Co-locating the
 * domain types here keeps every cross-file consumer importing from a single canonical
 * path. Helper functions previously bundled with types (sortUsersByAvatar, etc.) live
 * in `src/utils/userUtil.ts` so this file stays pure-type.
 */

export type Translations = Record<string, string>;

export type ConnectionStatus = "connecting" | "open" | "closed" | "error";

export type VoiceSessionState =
  | "idle"
  | "connecting"
  | "recording"
  | "streaming"
  | "error";

export interface CoreConfig {
  baseUrl: string;
  widgetId: string;
  /**
   * Optional embed-placement identity for this placement. `widget-id` stays the bot
   * identity (API calls, contact continuity); `placement-id` distinguishes two
   * placements of the same bot on one page so their per-placement UI/session state
   * (current page, login-return page) doesn't collide. The effective placement key —
   * `placementId || widgetId` — is the TARGETING identity (postMessage, getCore()).
   * Persisted per-placement UI state is scoped separately, by `widgetId` PLUS
   * `placementId` in the default UI, so two different bots reusing the same
   * `placement-id` string never share storage.
   */
  placementId?: string;
  userEmail?: string;
  startMessage?: string;
  hubIdentity?: string;
  skipIngest?: boolean;
  homeUrl?: string;
}

export interface ThreadSummary {
  id: number;
  rating: number | null;
  closedAt: string | null;
}

// --- Domain types (previously in src/models/*.ts) ---------------------------

export interface Attachment {
  url: string;
  size: string;
  type: string;
  filename: string;
}

export interface AuthenticationToken {
  token: string;
}

export interface Contact {
  uuid: string;
  email: string | null;
  verified: boolean;
}

export enum PresenceStatus {
  ONLINE = "online",
  ON_BREAK = "on_break",
  OFFLINE = "offline",
}

export interface User {
  id: number;
  avatar_url: string | null;
  first_name: string | null;
  last_name: string | null;
  presence_status: PresenceStatus;
}

export type MessageRole = "user" | "assistant" | "event";

export type MetadataType = "images" | "options" | "products" | "calendar";

export interface CalendarDayData {
  date: string;   // ISO date "2026-06-22"
  slots: string[]; // UTC ISO datetime strings, sorted ascending
}

export interface CalendarData {
  week_monday: string;  // ISO date of the Monday
  lang: string;         // "fi" | "sv" | "en"
  days: CalendarDayData[]; // 7 entries, Mon–Sun
  // IANA zone (e.g. "Europe/Helsinki") the slots are offered in. Slot times and
  // the click message are shown in this zone rather than the browser's. Absent
  // from older servers, in which case the browser-local zone is used (LAS-4733).
  timezone?: string;
}

export type MetadataEvent = "agent_joined" | "agent_invited";

// Attribution for a Tool function executed by the intent workflow, surfaced on
// the final assistant answer's metadata so the widget can credit which tools
// produced the response (LAS-1350). The backend deduplicates these by `function`
// and merges them into the message metadata.
export interface ToolUsed {
  function: string;
  label?: string;
  tool_name?: string;
}

// Recommended floating-popup size for rich content the backend emitted on this
// message (product cards, mini calendar). The widget grows the floating popup
// to fit, clamped by its own mode policy (see ChatWidget's resize effect,
// LAS-1653). Both keys are CSS length strings; either may be absent.
export interface LayoutHint {
  min_width?: string;
  min_height?: string;
}

export interface Metadata {
  options: string[];
  data: unknown;
  type?: MetadataType;
  event?: MetadataEvent;
  field_name?: string;
  tools_used?: ToolUsed[];
  layout_hint?: LayoutHint | null;
}

export interface Message {
  id: number;
  updated_at: string;
  // Null for a reply the backend has created but not rendered yet (and for one
  // it never renders). ChatMessageList filters these out before rendering.
  contents: string | null;
  is_finished: boolean | null;
  role: MessageRole;
  metadata: Metadata | null;
  sender: User | null;
  attachment: Attachment | null;
}

export interface Task {
  id: number;
  closed_at: string | null;
}

export interface MessageThread {
  id: number;
  created_at: string;
  rating: number | null;
  contact: Contact;
  messages: Message[];
  task: Task;
  assigned_user: User | null;
}

export interface VoiceSession {
  token: string;
  url: string;
}

export interface Colors {
  primary: string;
  secondary: string;
  background: string;
  text: string;
  /** Launcher gradient start colour (hex); absent on servers that predate LAS-4752. */
  toggle_primary?: string;
  /** Launcher gradient end colour (hex); absent on servers that predate LAS-4752. */
  toggle_accent?: string;
}

export interface BrandInformation {
  logo: string | null;
  avatar: string | null;
  colors: Colors;
  show_avatar_in_minimized: boolean;
}

export interface Widget {
  identifier: string;
  brand_information: BrandInformation;
  contact: Contact | null;
  voice_mode_available: boolean;
  /** Absent from servers that predate LAS-3469; treat absent as enabled. */
  attachments_enabled?: boolean;
  conversation_starters: [string, string][];
  average_response_time: number;
  support_agents: User[];
  translations: Translations;
  theme: "default" | "dna";
}
